<template>
    <form class="c-form" @submit.prevent="onSubmit" v-preloader="loading">
        <div class="c-form__body contact-us">
            <div class="c-control-group">
                <label :for="`${uniqId}_name`" class="c-control-label">{{ t('Name') }}: <span v-if="!contact.name" class="u-text-danger">*</span></label>
                <input :id="`${uniqId}_name`" :placeholder="t('Name')" type="text" class="c-input" name="name" v-model="contact.name" :class="{'has-error': errors.name !==''}">
                <p class="c-help-block has-error" v-if="errors.name" v-text="errors.name"></p>
            </div>
            <div class="c-control-group">
                <label :for="`${uniqId}_from`" class="c-control-label">{{ t('Email') }}: <span v-if="contact.from === null || contact.from === undefined" class="u-text-danger">*</span></label>
                <input :id="`${uniqId}_from`" :placeholder="t('Email')" type="email" class="c-input" name="from" v-model="contact.from" :class="{'has-error': errors.from !==''}">
                <p class="c-help-block has-error" v-if="errors.from" v-text="errors.from"></p>
            </div>
            <div class="c-control-group">
                <label :for="`${uniqId}_phone`" class="c-control-label">{{ t('Phone') }}:</label>
                <input :id="`${uniqId}_phone`" :placeholder="t('Phone')" type="text" class="c-input" name="phone" v-model="contact.phone">
            </div>
            <div class="c-control-group">
                <label :for="`${uniqId}_message`" class="c-control-label">{{ t('Message') }}: <span v-if="contact.message === null || contact.message === undefined" class="u-text-danger">*</span></label>
                <textarea :id="`${uniqId}_message`" :placeholder="t('Message')" class="c-textarea" name="message" rows="6" v-model="contact.message" :class="{'has-error': errors.message !==''}"></textarea>
                <p class="c-help-block has-error" v-if="errors.message" v-text="errors.message"></p>
            </div>

            <div class="c-control-group" v-if="captcha.key">
                <p class="c-help-block has-error" v-if="captcha.title" v-text="captcha.title"></p>
                <captcha :sitekey="captcha.key" @response="onCaptchaResponse" :size="'compact'"></captcha>
            </div>

            <div class="c-control-group u-mb-0">
                <button type="submit" class="c-button c-button--large  c-button--white-font  c-button--rounded c-button--primary c-button--block c-button--tall">
                    {{ t('Send') }}
                </button>
            </div>
        </div>
    </form>
</template>
<script lang="ts">
import has from 'lodash/has';
import { Component, Prop } from 'vue-property-decorator';
import { showPopup } from '../directives/popup';
import { dataLayer } from '../globals';
import Contact from '../models/Contact';
import BaseComponent from './Base';
import Captcha from './Recaptcha';

@Component({
        components: {
            Captcha,
        },
    })
    export default class ContactForm extends BaseComponent {
        @Prop({ type: String, default: 'Contact Form' }) protected readonly gtmCategory: string;

        protected loading: boolean = false;
        protected validate: boolean = false;
        protected captchaResponse: any = null;
        protected contact = new Contact();

        protected errors: { [key: string]: string } = {
            name: '',
            from: '',
            message: '',
        };

        protected captcha: { title: string, key: string } = {
            title: '',
            key: '',
        };

        protected async onSubmit(): Promise<boolean> {
            this.loading = true;
            this.validate = true;
            try {
                if (await this.validateContact()) {
                    const response = await this.contact.save(this.captchaResponse);
                    if (!response.success && has(response, 'body.key') && has(response, 'body.title')) {
                        this.captcha = response.body;
                    } else {
                        this.captcha.key = '';
                        this.successPopup();
                        dataLayer.push({
                            eventAction: 'Sent',
                            eventCategory: this.gtmCategory,
                            event: 'contactForm:sent',
                        }, {
                          event: 'form_contact',
                          form_name: this.contact.name,
                          form_email: this.contact.from,
                          form_phone: this.contact.phone,
                        });
                        this.contact.clear();
                    }
                }
            } catch (e) {
                console.warn(e);
                this.errorPopup(e);
            }
            this.loading = false;
            return true;
        }

        protected onCaptchaResponse(captchaResponse: any): void {
            this.captchaResponse = captchaResponse;
            this.captcha.title = '';
        }

        protected async validateContact(): Promise<boolean> {
            if (!this.validate) {
                return true;
            }
            const errors = {
                name: '',
                from: '',
                message: '',
            };
            try {
                await this.contact.validate();
                this.errors = errors;
                return true;
            } catch {
                this.contact.collectErrors().forEach((collection) => {
                    collection.path.forEach((elName) => {
                        errors[elName] = collection.errors.map((error) => error.message).join('<br>');
                    });
                });
                this.errors = errors;
                return false;
            }
        }

        protected mounted(): void {
            this.$watch('contact', this.validateContact, { deep: true });
        }

        protected successPopup(): void {
            const popup: any = {
                template: `
                <div class="v-modal c-notification">
                    <div class="v-modal__close" slot="top-right">
                        <button class="v-modal__close-button" @click="$emit('close')">
                            <svg xmlns="http://www.w3.org/2000/svg" version="1" viewBox="0 0 24 24"><path d="M13 12l5-5-1-1-5 5-5-5-1 1 5 5-5 5 1 1 5-5 5 5 1-1z"></path></svg>
                        </button>
                    </div>
                    <div class="v-modal__content">
                        <h3 class="u-text-center u-text-success" v-text="message"></h3>
                    </div>
                </div>`,
                props: ['message'],
            };

            showPopup(popup,
                {
                    message: this.t('Your message was sent successfully. Thanks.'),
                },
                {
                    width: 640,
                    height: '30%',
                    // clickToClose: false,
                    adaptive: true,
                    scrollable: true,
                    pivotY: 0.3,
                },
            );
        }

        protected errorPopup(errors): void {
            const popup: any = {
                template: `
                <div class="v-modal c-notification">
                    <div class="v-modal__close" slot="top-right">
                        <button class="v-modal__close-button" @click="$emit('close')">
                            <svg xmlns="http://www.w3.org/2000/svg" version="1" viewBox="0 0 24 24"><path d="M13 12l5-5-1-1-5 5-5-5-1 1 5 5-5 5 1 1 5-5 5 5 1-1z"></path></svg>
                        </button>
                    </div>
                    <div class="v-modal__content">
                        <strong v-for="error in errors" class="u-text-center u-text-danger" v-text="error.message"></strong>
                    </div>
                </div>`,
                props: ['errors'],
            };

            showPopup(popup,
                {
                    errors,
                },
                {
                    width: 640,
                    height: '30%',
                    // clickToClose: false,
                    adaptive: true,
                    scrollable: true,
                    pivotY: 0.3,
                },
            );
        }
    }
</script>
<style lang="scss">
.c-form .contact-us {
    background: #fdf3e9;
}

.contact-us {
    border-radius: 12px;

    .c-control-group {
        position: relative;
    }

    .c-control-label {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        color: transparent;
        margin-left: 11px;
        margin-bottom: 0;

        .u-text-danger {
            display: inline-block;
            margin-top: 10px;
        }
    }

    .c-input {
        height: 58px;
    }

    .c-input, .c-textarea {
        //margin-top: -30px;
        padding: 6px 10px;
        border-radius: 8px;
    }

    .c-textarea {
        padding-top: 16px;
        padding-bottom: 16px;
    }
}

input.mandatory {
    padding: 2px;
    height: 30px;
    width: 200px;
    background-image: url("http://www.fileformat.info/info/unicode/char/002a/asterisk.png");
    background-position: 160px -10px;
    background-repeat: no-repeat;
    background-size: 30%;
}

::-webkit-input-placeholder:after {
    content: '*';
}

:-moz-placeholder:after { /* Firefox 18- */
    content: '*';
}

::-moz-placeholder:after {  /* Firefox 19+ */
    content: '*';
}
</style>
